import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { productData, getAllCategories, getCategoryProducts } from '../data/productData';
import Header from '../components/common/Header';
import EmailModal from '../components/ui/EmailModal';
import { ROUTES, STORAGE_KEYS, CONTACT_EMAIL } from '../constants';
import { createMailtoLink, getStringFromLocalStorage, saveStringToLocalStorage } from '../utils';

export default function Products() {
  const { category } = useParams();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customizationData, setCustomizationData] = useState({
    size: '',
    color: '',
    personalMessage: '',
    specialRequirements: ''
  });
  const [userEmail, setUserEmail] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(category || 'kitchenware');
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Load saved email from localStorage on component mount
  useEffect(() => {
    const savedEmail = getStringFromLocalStorage(STORAGE_KEYS.USER_EMAIL);
    if (savedEmail) {
      setUserEmail(savedEmail);
    }
  }, []);

  // Update current category when URL parameter changes
  useEffect(() => {
    if (category && getAllCategories().includes(category)) {
      setCurrentCategory(category);
    }
  }, [category]);

  const currentProducts = getCategoryProducts(currentCategory);
  const categoryInfo = productData[currentCategory];

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setSelectedImageIndex(0);
    setCustomizationData({
      size: '',
      color: '',
      personalMessage: '',
      specialRequirements: ''
    });
  };

  const handleCustomizationChange = (field, value) => {
    setCustomizationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSendCustomization = () => {
    if (!userEmail) {
      setShowEmailModal(true);
    } else {
      sendCustomizationEmail();
    }
  };

  const handleEmailSubmit = (email) => {
    setUserEmail(email);
    saveStringToLocalStorage(STORAGE_KEYS.USER_EMAIL, email);
    setShowEmailModal(false);
    sendCustomizationEmail(email);
  };

  const sendCustomizationEmail = (email = userEmail) => {
    // Create email content
    const subject = `Product Customization Request - ${selectedProduct.name}`;
    const body = `
Dear Lokvir Impex Team,

I would like to customize the following product:

Product: ${selectedProduct.name}

Customization Details:
- Size: ${customizationData.size || 'Not specified'}
- Color: ${customizationData.color || 'Not specified'}
- Personal Message: ${customizationData.personalMessage || 'None'}
- Special Requirements: ${customizationData.specialRequirements || 'None'}

Customer Email: ${email}

Please contact me with pricing and availability.

Best regards,
Customer
    `;

    // Create mailto link
    const mailtoLink = createMailtoLink(CONTACT_EMAIL, subject, body);
    window.location.href = mailtoLink;

    // Reset form
    setCustomizationData({
      size: '',
      color: '',
      personalMessage: '',
      specialRequirements: ''
    });
    
    alert('Email client opened with your customization request. Please send the email to complete your request.');
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fafcf8] via-[#f5f9f0] to-[#eef3e7]" style={{ fontFamily: '"Plus Jakarta Sans", "Noto Sans", sans-serif' }}>
      {/* Enhanced Header */}
      <Header showBackButton={true} backButtonText="Back to Home" />

      {/* Enhanced Hero Section */}
      <div className="relative py-16 px-6">
        <div className="absolute inset-0 bg-gradient-to-r from-[#9be830]/5 to-[#79974e]/5"></div>
        <div className="relative max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-black text-[#151b0e] mb-4 leading-tight">
            Premium <span className="bg-gradient-to-r from-[#9be830] to-[#79974e] bg-clip-text text-transparent">Bamboo</span> Collection
          </h1>
          <p className="text-xl text-[#79974e] max-w-2xl mx-auto mb-8 leading-relaxed">
            Discover sustainable luxury with our handcrafted bamboo products designed for modern living
          </p>
        </div>
      </div>

      {/* Enhanced Category Navigation */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-[#eef3e7] px-6 py-6 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-[#151b0e] mb-6">Browse Categories</h2>
          <div className="flex flex-wrap gap-3">
            {getAllCategories().map((cat) => {
              const isActive = currentCategory === cat;
              return (
                <Link
                  key={cat}
                  to={`${ROUTES.PRODUCTS}/${cat}`}
                  className={`group relative px-6 py-3 rounded-2xl font-semibold transition-all transform hover:scale-[1.05] ${
                    isActive 
                      ? 'bg-gradient-to-r from-[#9be830] to-[#79974e] text-white shadow-lg' 
                      : 'bg-white text-[#79974e] hover:bg-gradient-to-r hover:from-[#9be830] hover:to-[#79974e] hover:text-white border-2 border-[#eef3e7] hover:border-transparent'
                  }`}
                >
                  <span className="relative z-10">{productData[cat].categoryName}</span>
                  {isActive && (
                    <div className="absolute inset-0 bg-gradient-to-r from-[#9be830] to-[#79974e] rounded-2xl blur opacity-30"></div>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Enhanced Category Header */}
        <div className="mb-12 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-[#151b0e] mb-3">{categoryInfo?.categoryName}</h1>
          <p className="text-[#79974e] text-lg max-w-2xl mx-auto leading-relaxed">{categoryInfo?.description}</p>
          <div className="w-24 h-1 bg-gradient-to-r from-[#9be830] to-[#79974e] mx-auto mt-6 rounded-full"></div>
        </div>

        {/* Enhanced Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {currentProducts.map((product, index) => (
                <div 
                  key={product.id}
                  className="group relative bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 cursor-pointer transform hover:scale-[1.02]"
                  onClick={() => handleProductClick(product)}
                  onMouseEnter={() => setHoveredProduct(product.id)}
                  onMouseLeave={() => setHoveredProduct(null)}
                  style={{
                    animationDelay: `${index * 100}ms`
                  }}
                >
                  {/* Product Image */}
                  <div className="relative h-80 md:h-96 overflow-hidden bg-[#f5f9f0] flex items-center justify-center">
                    <img 
                      src={product.images && product.images.length > 0 ? product.images[0] : product.image}
                      alt={product.name}
                      className="max-w-full max-h-full w-auto h-auto object-contain transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="bg-white text-[#151b0e] px-4 py-2 rounded-full font-semibold transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                        View Details
                      </div>
                    </div>
                  </div>
                  
                  {/* Product Info */}
                  <div className="p-6">
                    <h3 className="font-bold text-[#151b0e] mb-2 text-lg group-hover:text-[#79974e] transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-[#79974e] text-sm leading-relaxed line-clamp-2">
                      {product.description}
                    </p>
                    
                    {/* Features */}
                    <div className="flex flex-wrap gap-1 mt-3">
                      {product.features.slice(0, 2).map((feature, idx) => (
                        <span key={idx} className="text-xs bg-[#eef3e7] text-[#79974e] px-2 py-1 rounded-full">
                          {feature}
                        </span>
                      ))}
                      {product.features.length > 2 && (
                        <span className="text-xs bg-[#eef3e7] text-[#79974e] px-2 py-1 rounded-full">
                          +{product.features.length - 2} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Selection Indicator */}
                  {selectedProduct?.id === product.id && (
                    <div className="absolute inset-0 border-4 border-[#9be830] rounded-3xl pointer-events-none">
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#9be830] rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 256 256">
                          <path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/>
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              ))}
        </div>

        {/* Enhanced Product Details Modal */}
        {selectedProduct && (
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto animate-fadeIn"
            onClick={() => setSelectedProduct(null)}
          >
            <div 
              className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto my-8 transform animate-scaleIn"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="grid md:grid-cols-2 gap-8 p-6 md:p-10">
                {/* Left Side - Image Gallery */}
                <div className="space-y-6">
                  {/* Main Image */}
                  <div className="relative bg-[#f5f9f0] rounded-2xl shadow-lg flex items-center justify-center overflow-hidden aspect-square">
                    <img 
                      src={
                        selectedProduct.images && selectedProduct.images.length > 0 
                          ? selectedProduct.images[selectedImageIndex] 
                          : selectedProduct.image
                      }
                      alt={selectedProduct.name}
                      className="max-w-full max-h-full w-auto h-auto object-contain transition-all duration-300"
                    />
                    
                    {/* Image Navigation Arrows */}
                    {selectedProduct.images && selectedProduct.images.length > 1 && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImageIndex((prev) => 
                              prev === 0 ? selectedProduct.images.length - 1 : prev - 1
                            );
                          }}
                          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white text-[#151b0e] rounded-full flex items-center justify-center transition-all z-10 shadow-lg hover:scale-110"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256">
                            <path d="M165.66,202.34a8,8,0,0,1-11.32,11.32l-80-80a8,8,0,0,1,0-11.32l80-80a8,8,0,0,1,11.32,11.32L91.31,128Z"/>
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImageIndex((prev) => 
                              prev === selectedProduct.images.length - 1 ? 0 : prev + 1
                            );
                          }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white text-[#151b0e] rounded-full flex items-center justify-center transition-all z-10 shadow-lg hover:scale-110"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256">
                            <path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"/>
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* Thumbnail Gallery */}
                  {selectedProduct.images && selectedProduct.images.length > 1 && (
                    <div className="flex gap-4 justify-center flex-wrap">
                      {selectedProduct.images.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedImageIndex(idx)}
                          className={`w-28 h-28 md:w-32 md:h-32 rounded-xl overflow-hidden border-3 bg-[#f5f9f0] flex items-center justify-center transition-all hover:scale-105 ${
                            selectedImageIndex === idx 
                              ? 'border-[#9be830] scale-110 shadow-xl ring-4 ring-[#9be830]/40 border-4' 
                              : 'border-2 border-gray-200 hover:border-[#79974e] opacity-70 hover:opacity-100'
                          }`}
                        >
                          <img 
                            src={img}
                            alt={`${selectedProduct.name} view ${idx + 1}`}
                            className="max-w-full max-h-full w-auto h-auto object-contain"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Right Side - Product Details */}
                <div className="space-y-6">
                  {/* Close Button */}
                  <div className="flex justify-end">
                    <button
                      onClick={() => setSelectedProduct(null)}
                      className="w-10 h-10 bg-[#eef3e7] hover:bg-[#79974e] text-[#79974e] hover:text-white rounded-full flex items-center justify-center transition-all hover:scale-110"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
                        <path d="M208.49,191.51a12,12,0,0,1-17,17L128,145,64.49,208.49a12,12,0,0,1-17-17L111,128,47.51,64.49a12,12,0,0,1,17-17L128,111l63.51-63.52a12,12,0,0,1,17,17L145,128Z"/>
                      </svg>
                    </button>
                  </div>

                  {/* Product Title & Badge */}
                  <div>
                    <h3 className="text-3xl md:text-4xl font-bold text-[#151b0e] mb-3">{selectedProduct.name}</h3>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-sm font-semibold text-[#79974e] bg-gradient-to-r from-[#eef3e7] to-[#f5f9f0] px-4 py-2 rounded-full border border-[#eef3e7]">
                        Premium Quality
                      </span>
                    </div>
                    <p className="text-[#79974e] text-base leading-relaxed">{selectedProduct.description}</p>
                  </div>

                  {/* Enhanced Features */}
                  <div>
                    <h4 className="font-bold text-[#151b0e] mb-4 flex items-center gap-2 text-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
                        <path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/>
                      </svg>
                      Key Features
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedProduct.features.map((feature, index) => (
                        <div key={index} className="text-sm bg-gradient-to-r from-[#eef3e7] to-[#f5f9f0] text-[#79974e] px-4 py-3 rounded-xl border border-[#eef3e7] font-medium">
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Enhanced Customization Form */}
                  <div className="border-t-2 border-[#eef3e7] pt-6">
                    <h4 className="font-bold text-[#151b0e] mb-6 flex items-center gap-2 text-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
                        <path d="M224,48H168a40,40,0,0,0-32,16A40,40,0,0,0,104,48H32A16,16,0,0,0,16,64V192a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64A16,16,0,0,0,224,48ZM96,144H48V96H96Zm112,0H160V96h48Z"/>
                      </svg>
                      Customize Product
                    </h4>
                    
                    <div className="space-y-5">
                      <div>
                        <label className="block text-sm font-semibold text-[#151b0e] mb-2">Size Preference</label>
                        <input
                          type="text"
                          value={customizationData.size}
                          onChange={(e) => handleCustomizationChange('size', e.target.value)}
                          placeholder="e.g., Small, Medium, Large"
                          className="w-full px-4 py-3 border-2 border-[#eef3e7] rounded-xl text-sm focus:outline-none focus:border-[#9be830] focus:ring-2 focus:ring-[#9be830]/20 transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#151b0e] mb-2">Color Preference</label>
                        <input
                          type="text"
                          value={customizationData.color}
                          onChange={(e) => handleCustomizationChange('color', e.target.value)}
                          placeholder="e.g., Natural, Dark, Light"
                          className="w-full px-4 py-3 border-2 border-[#eef3e7] rounded-xl text-sm focus:outline-none focus:border-[#9be830] focus:ring-2 focus:ring-[#9be830]/20 transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#151b0e] mb-2">Personal Message</label>
                        <textarea
                          value={customizationData.personalMessage}
                          onChange={(e) => handleCustomizationChange('personalMessage', e.target.value)}
                          placeholder="Any text you'd like engraved or printed"
                          className="w-full px-4 py-3 border-2 border-[#eef3e7] rounded-xl text-sm focus:outline-none focus:border-[#9be830] focus:ring-2 focus:ring-[#9be830]/20 transition-all h-24 resize-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#151b0e] mb-2">Special Requirements</label>
                        <textarea
                          value={customizationData.specialRequirements}
                          onChange={(e) => handleCustomizationChange('specialRequirements', e.target.value)}
                          placeholder="Any special requirements or modifications"
                          className="w-full px-4 py-3 border-2 border-[#eef3e7] rounded-xl text-sm focus:outline-none focus:border-[#9be830] focus:ring-2 focus:ring-[#9be830]/20 transition-all h-24 resize-none"
                        />
                      </div>
                    </div>

                    <button
                      onClick={handleSendCustomization}
                      className="w-full mt-6 bg-gradient-to-r from-[#9be830] to-[#79974e] text-white py-4 rounded-2xl font-bold hover:shadow-xl transform hover:scale-[1.02] transition-all flex items-center justify-center gap-2 text-lg"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
                        <path d="M224,48H32a8,8,0,0,0-8,8V192a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A8,8,0,0,0,224,48Zm-96,85.15L52.57,64H203.43ZM98.71,128,40,181.81V74.19Zm11.84,10.85,12,11.05a8,8,0,0,0,10.82,0l12-11.05,58,53.15H52.57ZM157.29,128,216,74.18V181.82Z"/>
                      </svg>
                      Send Customization Request
                    </button>

                    {userEmail && (
                      <div className="mt-4 p-3 bg-gradient-to-r from-[#eef3e7] to-[#f5f9f0] rounded-xl">
                        <p className="text-xs text-[#79974e] flex items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 256 256">
                            <path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/>
                          </svg>
                          Saved email: {userEmail}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Email Modal */}
      {showEmailModal && (
        <EmailModal 
          onSubmit={handleEmailSubmit}
          onClose={() => setShowEmailModal(false)}
        />
      )}

      {/* Custom Styles */}
      <style jsx>{`
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}